import { PkPopover } from '@uefadigital/panenka-react';
import parseHtmlToReact from 'html-react-parser';
import { useRef, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { usePlaymakersOptions } from '../../store';
import FadeInAnimation from '../../utils/FadeInAnimation';
import { clickTracking } from '../../utils/tracking';

import { useCountry } from '@hooks/useCountry';
import ErrorBoundary from '@shared/ErrorBoundary';
import Modal from '@shared/Modal';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { IdpHandler } from '@user/IdpHandler';
import { orderBy } from '@utilities/collection-utils';
import type { D3Country } from '@utilities/country';
import { removeDiacritics } from '@utilities/text-manipulation';

import * as styles from './styles.module.scss';

const tooltipStates = Object.freeze({
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  UNAVAILABLE: 'unavailable',
});

const getCountryLabel = (t, code) => t(`country_${code?.toLowerCase()}`);

const TooltipContent = ({ country }) => {
  const {
    options: { activeCountries, excludedCountries },
  } = usePlaymakersOptions();
  const { t } = useTranslation();
  const { locale } = useParams();
  const [showRegistration, setShowRegistration] = useState(false);
  const idpHandler = useRef(new IdpHandler());

  useEffect(() => {
    idpHandler.current.init(window.idpApiKey, locale);
  }, [locale]);

  const fifaCountryCode = useMemo(() => country?.fifaCountryCode?.toLowerCase() || '', [country]);

  const countryActive = useMemo(() => {
    if (excludedCountries?.[fifaCountryCode]) return tooltipStates.UNAVAILABLE;

    if (activeCountries?.[fifaCountryCode]) return tooltipStates.ACTIVE;

    return tooltipStates.INACTIVE;
  }, [activeCountries, excludedCountries, fifaCountryCode]);

  const handleCloseModal = () => {
    setShowRegistration(false);

    GoogleTagManagerHelper.event(
      clickTracking.category,
      'Close',
      clickTracking.label,
      { section_reference: clickTracking.label, interaction: 'Close' },
      'playmakers',
    );
  };

  const openRegistration = () => {
    setShowRegistration(true);
  };

  const initRegistration = () => {
    idpHandler.current.waitIdpReady().then((idp) => {
      idp.showLiteRegistration({
        selector: '.js-registration-container',
        newsletterId: 'UEFAWomenFootball',
        campaign: window.location.pathname,
      });
    });
  };

  const label = useMemo(
    () => t(`tooltip_${countryActive}`)?.replace('{0}', getCountryLabel(t, country.fifaCountryCode)),
    [country.fifaCountryCode, countryActive, t],
  );
  const link = useMemo(
    () => activeCountries?.[fifaCountryCode] || excludedCountries?.[fifaCountryCode],
    [activeCountries, excludedCountries, fifaCountryCode],
  );
  const buttonLabel = useMemo(() => {
    const buttonLabels = {
      [tooltipStates.ACTIVE]: { tag: 'play-now', track: `Play now - ${fifaCountryCode.toUpperCase()}` },
      [tooltipStates.INACTIVE]: { tag: 'signup', track: `Sign up - ${fifaCountryCode.toUpperCase()}` },
      [tooltipStates.UNAVAILABLE]: { tag: 'find-out-more', track: `Find out more - ${fifaCountryCode.toUpperCase()}` },
    } as const;
    return buttonLabels[countryActive];
  }, [fifaCountryCode, countryActive]);

  const handleClick = () => {
    GoogleTagManagerHelper.event(
      clickTracking.category,
      buttonLabel?.track,
      clickTracking.label,
      { section_reference: clickTracking.label, interaction: buttonLabel?.track },
      'playmakers',
    );
    countryActive === tooltipStates.INACTIVE && openRegistration();
  };

  return (
    <>
      <Modal
        showModal={showRegistration}
        handleCloseModal={handleCloseModal}
        overlayClassName={styles.popup__overlay}
        className={styles.popup__content}
      >
        <div className={styles.popup__title}>{parseHtmlToReact(label ?? '')}</div>
        {showRegistration && (
          <div ref={initRegistration} className={`js-registration-container ${styles.registrationContainer}`} />
        )}
      </Modal>
      <div className={styles.captionM}>{parseHtmlToReact(label ?? '')}</div>
      <a
        className={`${styles['subscribe__tooltip-button']} ${styles[`subscribe__tooltip-button--${countryActive}`]}`}
        onClick={handleClick}
        href={link}
      >
        {t(buttonLabel?.tag)}
      </a>
    </>
  );
};

const CountrySelector = () => {
  const {
    options: { countries },
  } = usePlaymakersOptions();
  const { t } = useTranslation();
  const [country, setCountry] = useState<D3Country>();

  const userCountry = useCountry();

  useEffect(() => {
    userCountry?.fifaCountryCode &&
      countries.some((c) => c.fifaCountryCode === userCountry.fifaCountryCode) &&
      setCountry(userCountry);
  }, [countries, userCountry]);

  const handleChange = (e) => {
    setCountry(countries.find((c) => c.fifaCountryCode === e.target.value));
  };

  useEffect(() => {
    country &&
      GoogleTagManagerHelper.event(
        'Form',
        `Pick country - ${country.fifaCountryCode}`,
        clickTracking.label,
        { section_reference: clickTracking.label, interaction: `Pick country - ${country.fifaCountryCode}` },
        'playmakers',
      );
  }, [country]);

  const sortedCountries = useMemo(
    () => orderBy(countries, (c) => removeDiacritics(getCountryLabel(t, c.fifaCountryCode))),
    [countries, t],
  );

  return (
    <FadeInAnimation delay={0}>
      <PkPopover
        className={styles.subscribePopover}
        open={!!country}
        disableTriggerClick
        disableOutsideClick
        noCloseButton
        stroke='shadow'
      >
        <div slot='trigger' className={styles.subscribe__select}>
          <select value={country?.fifaCountryCode || ''} onChange={handleChange}>
            <option value=''>{t('select-country')}</option>
            {sortedCountries &&
              sortedCountries.map((c) => (
                <option key={`country_opt_${c.fifaCountryCode}`} value={c.fifaCountryCode}>
                  {getCountryLabel(t, c.fifaCountryCode)}
                </option>
              ))}
          </select>
        </div>
        <TooltipContent country={country} />
      </PkPopover>
    </FadeInAnimation>
  );
};

const Subscribe = () => {
  const { t } = useTranslation();
  return (
    <ErrorBoundary>
      <div id='playmakers' className={styles.section}>
        <div className={styles.section__content}>
          <div className={styles.subscribe__wrap}>
            <FadeInAnimation className={styles.subscribe__title}>{t('subscribe-title')}</FadeInAnimation>
            <FadeInAnimation className={styles.subscribe__subtitle}>{t('subscribe-subtitle')}</FadeInAnimation>
            <CountrySelector />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Subscribe;
